import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { MailCheck, ArrowLeft } from "lucide-react";

export default function Forgot() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [fallbackToken, setFallbackToken] = useState(null);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot", { email });
      setSent(true);
      if (!data.email_sent && data.reset_token) {
        setFallbackToken(data.reset_token); // dev fallback
        toast.info("Email service off — using dev token");
      } else {
        toast.success("Check your inbox for the reset link");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="max-w-md mx-auto w-full px-6 py-10">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft size={14} /> Back to login
        </Link>

        {!sent && (
          <>
            <h1 className="mt-8 font-manrope text-3xl font-extrabold tracking-tight text-slate-900">Reset your password</h1>
            <p className="text-slate-500 text-sm mt-2">Enter the email on your account and we'll send a reset link.</p>
            <form onSubmit={request} className="mt-8 space-y-4">
              <input data-testid="forgot-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-slate-900 outline-none" />
              <button data-testid="forgot-request-btn" disabled={busy} className="w-full bg-slate-900 text-white rounded-full py-4 font-semibold hover:bg-slate-800 active:scale-[0.98] transition-transform duration-150 disabled:opacity-60">{busy ? "Sending…" : "Send reset link"}</button>
            </form>
          </>
        )}

        {sent && (
          <div className="mt-10 text-center">
            <MailCheck className="mx-auto text-emerald-500" size={56} strokeWidth={1.5} />
            <h1 className="mt-6 font-manrope text-2xl font-bold text-slate-900">Check your email</h1>
            <p className="mt-2 text-slate-500 text-sm">If an account exists for <b className="text-slate-900">{email}</b>, we've sent a reset link. It expires in 30 minutes.</p>
            {fallbackToken && (
              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 text-left">
                <div className="font-semibold mb-1">Dev fallback token (email service unavailable)</div>
                <div className="font-mono break-all text-amber-800">{fallbackToken}</div>
                <Link to={`/reset?token=${encodeURIComponent(fallbackToken)}&email=${encodeURIComponent(email)}`} className="mt-2 inline-block text-slate-900 font-semibold underline underline-offset-2">Open reset page →</Link>
              </div>
            )}
            <Link to="/login" className="mt-8 inline-block text-sm text-slate-500 hover:text-slate-900 transition-colors">← Back to login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
